import React, {useReducer, useState} from 'react'
import flatMap from 'lodash.flatmap'
import qs from 'qs'
import {STATES} from 'lib/strings'
import {Checkbox} from 'antd'
import {Link} from 'react-router-dom'
import Heading from 'components/shared/Heading'
import SelectablePhotoThumb from 'components/shared/SelectablePhotoThumb'
import PhotoGallery from 'components/shared/PhotoGallery'
import DownloadZipDropdown from 'components/shared/DownloadZipDropdown'
import Button from 'components/shared/Button'
import './index.scss'

function correctionUrlLabel(key = null) {
  switch (key) {
    case 'cubitoolAlternative':
      return 'Alternativ plantegning'
    default:
      return 'Rediger plantegning'
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'markAsset': {
      return {
        ...state,
        selectedAssets: [...state.selectedAssets, action.asset]
      }
    }
    case 'unmarkAsset': {
      return {
        ...state,
        selectedAssets: state.selectedAssets.filter(asset => asset.id !== action.asset.id)
      }
    }
    case 'openPhotoPreview': {
      return {
        ...state,
        photoPreviewIndex: action.index,
      }
    }
    case 'closePhotoPreview': {
      return {
        ...state,
        photoPreviewIndex: null,
      }
    }
    case 'resetSelectedAssets': {
      return {
        ...state,
        selectedAssets: action.assets,
      }
    }
    default: {
      throw new Error(`Unknown action ${action}`)
    }
  }
}

const DeliveredFloorPlans = ({product, estateId, readOnly = false}) => {
  const [prevProductId, setPrevProductId] = useState(product.id)
  const [state, stateReducer] = useReducer(reducer, {
    selectedAssets: [],
    photoPreviewIndex: null,
  })

  if (product.id !== prevProductId) {
    stateReducer({type: 'resetSelectedAssets', assets: []})
    setPrevProductId(product.id)
  }

  const assetIsSelected = (asset) => {
    return !!state.selectedAssets.find(p => p.id === asset.id)
  }

  const assetSelectedChanged = (asset, selected) => {
    const type = selected ? 'markAsset' : 'unmarkAsset'
    stateReducer({type, asset})
  }

  const previewPhoto = (index) => stateReducer({type: 'openPhotoPreview', index})
  const closeGallery = () => stateReducer({type: 'closePhotoPreview'})

  const additionalCost = product.extraAssetUnitPrice && product.floors
    ? (state.selectedAssets.length - product.remainingCredits) * product.extraAssetUnitPrice
    : 0

  const planDrawings = product.assets.edges
    .filter(edge => edge.node.assets && edge.node.assets.edges.length)
    .map(({node}) => ({...node, selected: false}))

  const photos = flatMap(planDrawings, (planDrawing) => {
    return planDrawing.assets.edges.map((asset) =>
      asset.node.__typename === 'PhotoAsset' ? asset.node : null
    ).filter(asset => !!asset)
  })

  const assetStatusLabel = (photo) => {
    if (photo.state === 'purchased') return STATES.delivered
    if (photo.free) return 'Inkludert'
    return ''
  }

  const [correctionAsset] = product.assets.edges.map(e => e.node).filter(a => a.__typename === 'PlanDrawingCorrectionAsset')
  const anyAssetsToPurchase = planDrawings.some(p => p.state === 'available_for_purchase')
  const additionalCostLabel = additionalCost > 0 ? `(+ ${additionalCost}kr)` : ''

  let correctionUrls = []
  if (correctionAsset) {
    correctionUrls = [{
      name: correctionUrlLabel(),
      url: `${correctionAsset.url}&redirect=${encodeURIComponent(window.location.href)}`,
    }]
  } else if (product.correctionUrls) {
    correctionUrls = product.correctionUrls.map(u => ({
      name: correctionUrlLabel(u.name),
      url: u.url,
    }))
  }

  return (
    <div className="delivered-floor-plans">
      <div className="floors">
        {planDrawings.map((planDrawing, i) => {
          const photos = planDrawing.assets.edges.map(e => e.node).filter(a => a.image)
          const availableForPurchase = planDrawing.state === 'available_for_purchase' && !planDrawing.free

          return (
            <div key={planDrawing.id} className="floor">
              <Heading level={3}>Plantegning #{i + 1}</Heading>
              <SelectablePhotoThumb.Wrapper>
                {photos.map((photo) => (
                  <SelectablePhotoThumb
                    key={photo.id}
                    photo={photo}
                    isSelectable={false}
                    onClick={() => previewPhoto(i)}
                  />
                ))}
              </SelectablePhotoThumb.Wrapper>
              <div className="overlay">
                <span className="label">
                  {assetStatusLabel(planDrawing)}
                </span>
                <span>
                  {!readOnly && availableForPurchase && (
                    <Checkbox
                      checked={assetIsSelected(planDrawing)}
                      onChange={(e) => assetSelectedChanged(planDrawing, e.target.checked)}
                    />
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {!readOnly && anyAssetsToPurchase && (
        <div className="purchase-action">
          <Link to={{
            pathname: `/oppdrag/${estateId}/godkjenn-leveranse/${product.id}`,
            search: '?' + qs.stringify({
              assetIds: state.selectedAssets.map(p => p.id),
            }),
          }}>
            <Button
              type="navigation"
              disabled={state.selectedAssets.length === 0}
            >
              {state.selectedAssets.length === 0
                ? <>Du må velge minst én plantegning</>
                : <>Godkjenn {state.selectedAssets.length} plantegninger {additionalCostLabel}</>
              }
            </Button>
          </Link>
        </div>
      )}

      <div className="actions">
        <DownloadZipDropdown productId={product.id} />
        {!readOnly && correctionUrls.map(({name, url}, i) => (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              secondary
              type="action"
            >
              {name}
            </Button>
          </a>
        ))}
      </div>

      {state.photoPreviewIndex !== null && (
        <PhotoGallery
          images={photos.map(p => p.image.url)}
          initialImageIndex={state.photoPreviewIndex}
          onCloseRequest={closeGallery}
        />
      )}
    </div>
  )
}

export default DeliveredFloorPlans
